<template>
  <div class="filters-container">
    <ContentSkeleton v-if="isLoading" variant="lines" :rows="2" />
    <template v-else>
      <div class="filters-toolbar-block mb-2">
        <div
          v-if="!hideKeywordToolbar || !hideMoreFiltersToolbar"
          class="d-flex flex-wrap align-items-stretch align-items-md-center gap-2 filters-toolbar"
        >
          <div
            v-if="!hideKeywordToolbar"
            class="flex-grow-1"
            style="min-width: 220px; max-width: 36rem"
          >
            <div class="input-group input-group-sm">
              <span class="input-group-text py-1"
                ><i class="fas fa-search text-muted"></i
              ></span>
              <input
                type="text"
                v-model="textSearchQuery"
                class="form-control py-1"
                :placeholder="$t('KeywordSearch')"
                @input="onTextSearchChange"
                @keyup.enter="applyTextSearch"
              />
              <button
                v-if="textSearchQuery"
                class="btn btn-outline-secondary btn-sm py-1"
                type="button"
                @click="clearTextSearch"
                :title="$t('Clear')"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>
          </div>
          <button
            v-if="!hideMoreFiltersToolbar"
            type="button"
            class="btn btn-sm btn-outline-primary align-self-center flex-shrink-0"
            @click="toggleMoreFilters"
          >
            <i class="fas fa-sliders-h me-1"></i>{{ $t("MoreFilters") }}
            <i
              class="fas ms-1"
              :class="moreFiltersOpen ? 'fa-chevron-up' : 'fa-chevron-down'"
            ></i>
          </button>
        </div>
        <div
          v-if="appliedFilterChips.length > 0"
          class="applied-filters-toolbar w-100 mt-2 pt-2 border-top"
        >
          <div
            class="applied-filters-toolbar-inner d-flex flex-wrap align-items-center gap-1"
          >
            <span
              v-for="chip in appliedFilterChips"
              :key="chip.key"
              class="badge bg-primary text-white fw-normal applied-filter-chip"
              :class="{
                'applied-filter-chip--editable': chip.key !== '__keyword',
              }"
              :title="chip.text"
              @click="onAppliedChipClick(chip.key)"
            >
              <span class="applied-filter-chip-text text-truncate">{{
                chip.text
              }}</span>
              <button
                type="button"
                class="btn btn-sm applied-filter-chip-remove p-0 ms-1 align-baseline lh-1 text-white text-opacity-75 border-0 bg-transparent shadow-none"
                :title="$t('Clear')"
                @click.stop="onAppliedChipClear(chip.key)"
              >
                <i class="fas fa-times"></i>
              </button>
            </span>
          </div>
        </div>
      </div>

      <div
        v-show="moreFiltersOpen"
        class="filters-extra rounded border bg-white p-3 mt-1"
      >
        <p
          v-if="visibleFilterFields.length === 0"
          class="text-muted small mb-0"
        >
          —
        </p>
        <div v-else class="row g-3">
          <div
            v-for="field in visibleFilterFields"
            :key="field.key"
            class="col-12 col-md-6 col-xl-4"
          >
            <FiltersFieldControls
              :field="field"
              :model-name="modelName"
              :filter-value="filters[field.key]"
              :filter-operator="
                filterOperators[field.key] || defaultFilterOperator(field)
              "
              :temp-date-range="tempDateRanges[field.key]"
              :list-options="getListOptions(field)"
              :reference-options-list="
                internalReferenceOptions[field.key] || []
              "
              :users-options="usersOptions"
              :users-options-loading="usersOptionsLoading"
              :list-options-sub-field="getListOptionsForSubField(field)"
              :searching-reference="searchingFields.has(field.key)"
              :is-date-field="isDateField(field)"
              :filter-active="isFilterRowActive(field.key)"
              :multiselect-list-label="
                (o) => listMultiselectCustomLabel(o, field, false)
              "
              :multiselect-list-label-sub="
                (o) => listMultiselectCustomLabel(o, field, true)
              "
              :reference-multiselect-label="
                (opt) => getMultiselectLabel(opt, field.key)
              "
              @clear="clearFilter(field.key)"
              @debounced-input="onDebouncedFilterValueInput(field.key, $event)"
              @immediate-input="onFilterValueImmediate(field.key, $event)"
              @operator-change="onFilterOperatorImmediate(field.key, $event)"
              @date-operator-change="onDateOperatorChange(field.key, $event)"
              @date-range-input="onFilterPanelDateRangeInput(field.key, $event)"
              @clear-date-range="clearDateRange(field.key)"
              @reference-search="onReferenceSearchChange(field, $event)"
              @ref-subfield-number-input="
                onReferenceSubfieldNumberInput(field.key, $event)
              "
              @shipping-status-change="
                onShippingStatusFilterChange(field.key, $event)
              "
            />
          </div>
        </div>
      </div>

      <!-- Popup filtro por columna (cabecera tabla dinámica) -->
      <div
        v-if="
          columnFilterPopupOpen &&
          columnFilterPopupField &&
          columnFilterPopupDraft
        "
        class="modal fade show d-block gt-column-filter-modal"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
        style="background-color: rgba(0, 0, 0, 0.45)"
        @click.self="closeColumnFilterPopup"
      >
        <div
          class="modal-dialog modal-dialog-centered modal-lg gt-column-filter-modal-dialog"
          role="document"
          @click.stop
        >
          <div class="modal-content">
            <div class="modal-header py-2">
              <h5 class="modal-title text-truncate">
                {{ $t(columnFilterPopupField.label) }}
              </h5>
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary border-0"
                :aria-label="$t('Close')"
                @click="closeColumnFilterPopup"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>
            <div class="modal-body">
              <FiltersFieldControls
                :field="columnFilterPopupField"
                :model-name="modelName"
                :filter-value="columnFilterPopupDraft.filterValue"
                :filter-operator="columnFilterPopupDraft.filterOperator || '='"
                :temp-date-range="
                  columnFilterPopupDraft.tempDateRange || {
                    start: '',
                    end: '',
                  }
                "
                :list-options="getListOptions(columnFilterPopupField)"
                :reference-options-list="
                  internalReferenceOptions[columnFilterPopupField.key] || []
                "
                :users-options="usersOptions"
                :users-options-loading="usersOptionsLoading"
                :list-options-sub-field="
                  getListOptionsForSubField(columnFilterPopupField)
                "
                :searching-reference="
                  searchingFields.has(columnFilterPopupField.key)
                "
                :is-date-field="isDateField(columnFilterPopupField)"
                :filter-active="isColumnPopupDraftActive"
                hide-label-row
                :multiselect-list-label="
                  (o) =>
                    listMultiselectCustomLabel(o, columnFilterPopupField, false)
                "
                :multiselect-list-label-sub="
                  (o) =>
                    listMultiselectCustomLabel(o, columnFilterPopupField, true)
                "
                :reference-multiselect-label="
                  (opt) => getMultiselectLabel(opt, columnFilterPopupField.key)
                "
                @clear="clearColumnPopupDraftFilter"
                @debounced-input="onColumnPopupDraftDebouncedInput"
                @immediate-input="onColumnPopupDraftImmediateInput"
                @operator-change="onColumnPopupDraftOperatorChange"
                @date-operator-change="onColumnPopupDraftDateOperatorChange"
                @date-range-input="onColumnPopupDraftDateRangeInput"
                @clear-date-range="clearColumnPopupDraftDateRange"
                @reference-search="
                  onReferenceSearchChange(columnFilterPopupField, $event)
                "
                @ref-subfield-number-input="onColumnPopupDraftRefSubfieldNumber"
                @shipping-status-change="onColumnPopupDraftShippingStatus"
              />
            </div>
            <div class="modal-footer py-2 d-flex gap-2 justify-content-end">
              <button
                type="button"
                class="btn btn-secondary btn-sm"
                @click="closeColumnFilterPopup"
              >
                {{ $t("Close") }}
              </button>
              <button
                type="button"
                class="btn btn-primary btn-sm"
                @click="applyColumnFilterPopup"
              >
                <i class="fas fa-filter me-1"></i>{{ $t("Filters") }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { wencesApi } from "@/api/wencesApi";
import { i18n } from "@/i18n";
import { getStatusLabel } from "@/utils/shippingStatus";
import { resolveReferenceSubfieldFilterField } from "@/utils/resolveReferenceSubfieldPath";
import { coerceBooleanFilterValue } from "@/utils/coerceBooleanFilterValue";
import {
  isFilterSpecActive,
  LEGACY_DATE_RANGE_SEPARATOR,
  legacyDateRangeFromValue,
  stripLegacyBetweenPrefix,
  specFromLegacyState,
} from "@/utils/columnFilterSpec";
import FiltersFieldControls from "./FiltersFieldControls.vue";
import {
  isDefaultFilterField,
  defaultGroupFilterSubFields,
} from "@/utils/defaultFilterFields";
import ContentSkeleton from "@/components/common/ContentSkeleton.vue";

export default {
  name: "Filters",
  components: {
    ContentSkeleton,
    FiltersFieldControls,
  },
  props: {
    modelFields: {
      type: Array,
      required: true,
    },
    referenceOptions: {},
    viewId: {
      type: String,
      default: "",
    },
    modelName: {
      type: String,
      default: "",
    },
    selectedFilterFields: {
      type: Object,
      required: true,
    },
    isReportMode: {
      type: Boolean,
      default: false,
    },
    hideKeywordToolbar: {
      type: Boolean,
      default: false,
    },
    hideMoreFiltersToolbar: {
      type: Boolean,
      default: false,
    },
    keywordQuery: {
      type: String,
      default: "",
    },
    syncedMoreFiltersOpen: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      filters: {},
      filterOperators: {},
      internalReferenceOptions: {},
      lists: [],
      isLoading: true,
      moreFiltersOpen: false,
      tempDateRanges: {},
      filterEmitTimeouts: {},
      dateCommitTimeouts: {},
      saveTimeout: null,
      searchingFields: new Set(),
      searchTimeout: null,
      textSearchTimeout: null, // Timeout para debouncing de búsqueda de texto
      visibleFilterFields: [],
      textSearchQuery: "",
      referenceSubFieldsCache: {}, // Cache de campos de modelos referenciados
      usersOptions: [], // Lista de usuarios precargada (filtros reference→users y reference_subfield→users)
      usersOptionsLoading: false,
      _usersOptionsLoadPromise: null,
      referenceSearchCache: {}, // Cache para búsquedas de referencias (key: `${field.ref}:${searchTerm}`, value: results)
      filterModelDefinitionsCache: {},
      columnFilterPopupOpen: false,
      columnFilterPopupFieldKey: null,
      /** Borrador del popup de cabecera (aplica al pulsar «Filtrar»). */
      columnFilterPopupDraft: null,
      /** Definición del campo resuelta solo para el popup (sin sumar a «Más filtros»). */
      columnFilterPopupResolvedField: null,
      /** Meta de campos filtrados solo desde cabecera (emitFilters / chips cuando no están en visibleFilterFields). */
      appliedFilterFieldDefs: {},
      /** Tras aplicar filtro desde popup de cabecera: evita loadFilterPreferences() pisando valores recién guardados. */
      suppressFilterPreferencesOnce: false,
    };
  },
  computed: {
    /** Estado visual del chip «activo» en el popup según el borrador. */
    isColumnPopupDraftActive() {
      const d = this.columnFilterPopupDraft;
      if (!d) return false;
      const operator = d.filterOperator || "=";
      const value = d.filterValue;
      const hasValue =
        value !== null && value !== undefined && value !== "";
      const hasSpecial =
        operator === "is_null" || operator === "is_not_null";
      return hasSpecial || hasValue;
    },
    columnFilterPopupField() {
      if (this.columnFilterPopupResolvedField) {
        return this.columnFilterPopupResolvedField;
      }
      if (!this.columnFilterPopupFieldKey) return null;
      return (
        this.visibleFilterFields.find(
          (f) => f.key === this.columnFilterPopupFieldKey
        ) || null
      );
    },
    hasActiveFilters() {
      const fromFilters = Object.entries(this.filters).some(([key, value]) => {
        const operator = this.filterOperators[key] || "=";
        // Permitir false como valor válido (para campos booleanos)
        const hasValue = value !== null && value !== undefined && value !== "";
        const hasSpecialOperator =
          operator === "is_null" || operator === "is_not_null";
        return hasValue || hasSpecialOperator;
      });
      if (fromFilters) return true;
      // is_null/is_not_null pueden existir solo en filterOperators
      return Object.values(this.filterOperators || {}).some(
        (op) => op === "is_null" || op === "is_not_null"
      );
    },
    activeFilterCount() {
      return this.visibleFilterFields.filter((field) =>
        this.isFilterRowActive(field.key)
      ).length;
    },
    /** Chips de filtros activos debajo de búsqueda y «Más filtros». */
    appliedFilterChips() {
      const chips = [];
      const q = (this.textSearchQuery || "").trim();
      if (q) {
        chips.push({
          key: "__keyword",
          text: `${this.$t("KeywordSearch")}: ${q}`,
        });
      }
      for (const field of this.visibleFilterFields) {
        if (!this.isFilterRowActive(field.key)) continue;
        const text = this.getFilterDisplayText(field.key);
        if (text) {
          chips.push({ key: field.key, text });
        }
      }
      return chips;
    },
    nonGroupFields() {
      return this.modelFields.filter((field) => field.type !== "group");
    },
    groupFields() {
      return this.modelFields.filter((field) => field.type === "group");
    },
  },
  watch: {
    keywordQuery: {
      handler(val) {
        if (!this.hideKeywordToolbar) return;
        const next = val || "";
        if (this.textSearchQuery !== next) {
          this.textSearchQuery = next;
        }
      },
      immediate: true,
    },
    textSearchQuery(val) {
      if (!this.hideKeywordToolbar) return;
      const next = val || "";
      if ((this.keywordQuery || "") !== next) {
        this.$emit("update:keywordQuery", next);
      }
    },
    moreFiltersOpen(val) {
      if (!this.hideMoreFiltersToolbar) return;
      this.$emit("update:syncedMoreFiltersOpen", !!val);
    },
    syncedMoreFiltersOpen: {
      handler(val) {
        if (!this.hideMoreFiltersToolbar) return;
        const next = !!val;
        if (this.moreFiltersOpen !== next) {
          this.moreFiltersOpen = next;
        }
      },
      immediate: true,
    },
    selectedFilterFields: {
      async handler(newVal, oldVal) {
        // Inicializar filtros para los nuevos campos
        Object.keys(newVal || {}).forEach((key) => {
          if (newVal[key] && !(key in this.filters)) {
            this.$set(this.filters, key, null);
            const field = this.modelFields.find((f) => f.key === key);
            // Solo establecer operador por defecto si no existe ya uno establecido
            // (para evitar sobrescribir operadores establecidos al cargar reportes)
            if (!(key in this.filterOperators)) {
              this.$set(
                this.filterOperators,
                key,
                this.defaultFilterOperator(field)
              );
            }
            if (this.isDateField(field)) {
              this.$set(this.tempDateRanges, key, { start: "", end: "" });
            }
          }
        });

        // Actualizar campos visibles
        await this.updateVisibleFilterFields();

        // NO cargar preferencias generales si:
        // 1. Estamos en modo reporte (isReportMode)
        // 2. Es la primera ejecución (immediate) y selectedFilterFields ya tiene valores (viene de un reporte)
        const hasValues = newVal && Object.keys(newVal).length > 0;
        const isFirstRun = oldVal === undefined || oldVal === null;
        const isFromReport = isFirstRun && hasValues;

        if (this.suppressFilterPreferencesOnce) {
          this.suppressFilterPreferencesOnce = false;
          await this.loadReferenceOptions();
        } else if (
          this.modelName &&
          hasValues &&
          !this.isReportMode &&
          !isFromReport
        ) {
          await this.loadFilterPreferences();
          await this.loadReferenceOptions();
        } else if (this.isReportMode || isFromReport) {
          await this.loadReferenceOptions();
        }
      },
      immediate: true,
      deep: true,
    },
    modelFields: {
      async handler() {
        // Actualizar campos visibles cuando cambien los campos del modelo
        await this.updateVisibleFilterFields();
      },
      immediate: true,
    },
  },
  async mounted() {
    this.isLoading = true;
    try {
      await this.loadData();
      await this.updateVisibleFilterFields();
    } finally {
      this.isLoading = false;
    }
    this.$nextTick(() => {
      if (this.hasActiveFilters) {
        this.moreFiltersOpen = true;
        this.onMoreFiltersPanelOpened();
      }
    });
  },
  beforeDestroy() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    if (this.textSearchTimeout) {
      clearTimeout(this.textSearchTimeout);
    }
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }
    Object.values(this.filterEmitTimeouts || {}).forEach(clearTimeout);
    Object.values(this.dateCommitTimeouts || {}).forEach(clearTimeout);
  },
  methods: {
    $t(key) {
      return i18n.t(key);
    },
    // Helper para determinar si un campo es de tipo fecha (incluyendo reference_subfield)
    isDateField(field) {
      if (!field) return false;
      if (field.type === "date" || field.type === "datetime") return true;
      if (
        field.type === "reference_subfield" &&
        (field.subFieldType === "date" || field.subFieldType === "datetime")
      )
        return true;
      return false;
    },
    /**
     * Resuelve la definición de un campo anidado dentro de un grupo (ej: pathParts = ['nestedGroup', 'field']).
     * @returns { { fieldDef: object|null, labelParts: string[] } }
     */
    async getModelDefinitionForFilter(modelName) {
      if (!modelName) return null;
      const k = String(modelName).toLowerCase();
      if (this.filterModelDefinitionsCache[k]) {
        return this.filterModelDefinitionsCache[k];
      }
      try {
        const { data } = await wencesApi.get(
          `/api/gotrading/model-definitions/${modelName}`
        );
        if (data) {
          this.$set(this.filterModelDefinitionsCache, k, data);
          return data;
        }
      } catch (e) {
        console.error(
          `Error loading model definition for filter (${modelName}):`,
          e
        );
      }
      return null;
    },
    resolveNestedGroupField(groupField, pathParts) {
      if (!pathParts || pathParts.length === 0 || !groupField.fields) {
        return { fieldDef: null, labelParts: [] };
      }
      let currentFields = groupField.fields;
      const labelParts = [];
      for (let i = 0; i < pathParts.length; i++) {
        const part = pathParts[i];
        const isLast = i === pathParts.length - 1;
        const sub = currentFields.find((f) => f.key === part);
        if (!sub) return { fieldDef: null, labelParts: [] };
        const label = sub.label || sub.key || part;
        labelParts.push(typeof label === "string" ? this.$t(label) : label);
        if (isLast) {
          return { fieldDef: sub, labelParts };
        }
        if (sub.type !== "group" || !sub.fields) {
          return { fieldDef: null, labelParts: [] };
        }
        currentFields = sub.fields;
      }
      return { fieldDef: null, labelParts: [] };
    },
    async updateVisibleFilterFields() {
      const visibleFields = [];
      const seenKeys = new Set(); // Para evitar duplicados

      // Si no hay campos seleccionados específicamente, mostrar campos básicos por defecto
      // PERO NO si estamos en modo reporte (esperar a que se carguen los campos del reporte)
      if (
        (!this.selectedFilterFields ||
          Object.keys(this.selectedFilterFields).length === 0) &&
        !this.isReportMode
      ) {
        // Campos básicos comunes (la misma lista que el selector muestra
        // tildada cuando no hay selección; ver utils/defaultFilterFields).
        this.nonGroupFields.forEach((field) => {
          if (isDefaultFilterField(field) && !seenKeys.has(field.key)) {
            seenKeys.add(field.key);
            visibleFields.push(field);
          }
        });

        // También agregar algunos campos de grupo básicos
        this.groupFields.forEach((groupField) => {
          defaultGroupFilterSubFields(groupField).forEach((subField) => {
            const fieldKey = `${groupField.key}.${subField.key}`;
            if (!seenKeys.has(fieldKey)) {
              seenKeys.add(fieldKey);
              visibleFields.push({
                ...subField,
                key: fieldKey,
                label: `${i18n.t(groupField.label)} - ${i18n.t(
                  subField.label
                )}`,
              });
            }
          });
        });
      } else {
        // Agregar campos normales seleccionados (no reference)
        this.nonGroupFields.forEach((field) => {
          const isSelected = this.selectedFilterFields[field.key] === true;
          if (
            field.type !== "reference" &&
            isSelected &&
            !seenKeys.has(field.key)
          ) {
            seenKeys.add(field.key);
            visibleFields.push(field);
          }
        });

        // Agregar campos de tipo reference y sus subcampos seleccionados
        for (const field of this.nonGroupFields) {
          if (field.type === "reference") {
            // Si el campo reference principal está seleccionado
            if (
              this.selectedFilterFields[field.key] === true &&
              !seenKeys.has(field.key)
            ) {
              seenKeys.add(field.key);
              visibleFields.push(field);
            }

            // Buscar subcampos seleccionados del reference (e.g., contact.name)
            const hasSelectedSubfields = Object.keys(
              this.selectedFilterFields
            ).some(
              (key) =>
                key.startsWith(`${field.key}.`) &&
                this.selectedFilterFields[key] === true
            );

            // Si hay subcampos seleccionados, cargar el cache si no existe
            if (
              hasSelectedSubfields &&
              !this.referenceSubFieldsCache[field.key] &&
              field.ref
            ) {
              await this.loadReferenceSubFields(field.ref, field.key);
            }

            const refSelectedKeys = Object.keys(
              this.selectedFilterFields
            ).filter(
              (k) =>
                k.startsWith(`${field.key}.`) &&
                this.selectedFilterFields[k] === true
            );
            for (const key of refSelectedKeys) {
              if (seenKeys.has(key)) continue;
              seenKeys.add(key);
              const built = await resolveReferenceSubfieldFilterField(
                this.modelFields,
                key,
                (name) => this.getModelDefinitionForFilter(name)
              );
              if (built) {
                const leafDef = built.subFieldDef;
                const intermediate = built.intermediateRefField;
                let label;
                if (intermediate && built.nestedFieldKey) {
                  label = `${this.$t(field.label)} → ${this.$t(
                    intermediate.label || built.subFieldKey
                  )} → ${this.$t(leafDef.label || built.nestedFieldKey)}`;
                } else {
                  label = `${this.$t(field.label)} → ${this.$t(
                    leafDef.label || built.subFieldKey
                  )}`;
                }
                visibleFields.push({
                  key: built.key,
                  label,
                  type: "reference_subfield",
                  parentField: field,
                  subFieldKey: built.subFieldKey,
                  nestedFieldKey: built.nestedFieldKey || undefined,
                  subFieldType: built.subFieldType || "string",
                  subFieldRef: built.subFieldRef || null,
                  subFieldListType: built.subFieldListType || null,
                  subFieldListRef: built.subFieldListRef || null,
                  ref: field.ref,
                  isArray: built.isArray,
                });
              } else {
                const subFieldKey = key.replace(`${field.key}.`, "");
                const subFieldDef =
                  this.referenceSubFieldsCache?.[field.key]?.find(
                    (f) => f.key === subFieldKey
                  ) || {};
                visibleFields.push({
                  key: key,
                  label: `${this.$t(field.label)} → ${this.$t(
                    subFieldDef.label || subFieldKey
                  )}`,
                  type: "reference_subfield",
                  parentField: field,
                  subFieldKey: subFieldKey,
                  subFieldType: subFieldDef.type || "string",
                  subFieldRef: subFieldDef.ref || null,
                  subFieldListType: subFieldDef.listType || null,
                  subFieldListRef: subFieldDef.listRef || null,
                  ref: field.ref,
                });
              }
            }
          }
        }

        // Agregar campos de grupo seleccionados (incluyendo dos o más niveles de anidación)
        this.groupFields.forEach((groupField) => {
          // Recoger todas las claves seleccionadas que pertenecen a este grupo (ej: group.field, group.nestedGroup.field)
          const selectedKeysInGroup = Object.keys(
            this.selectedFilterFields
          ).filter(
            (key) =>
              key.startsWith(`${groupField.key}.`) &&
              this.selectedFilterFields[key] === true
          );

          selectedKeysInGroup.forEach((fieldKey) => {
            if (seenKeys.has(fieldKey)) return;
            const pathSuffix = fieldKey.replace(`${groupField.key}.`, "");
            const pathParts = pathSuffix.split(".");
            // Resolver la definición del campo recorriendo la jerarquía (soporta group.nestedGroup.field)
            const { fieldDef, labelParts } = this.resolveNestedGroupField(
              groupField,
              pathParts
            );
            if (!fieldDef || fieldDef.type === "group") return; // no filtrar por el grupo en sí, solo por sus hojas
            seenKeys.add(fieldKey);
            visibleFields.push({
              ...fieldDef,
              key: fieldKey,
              label:
                labelParts.length > 0
                  ? `${i18n.t(groupField.label)} → ${labelParts.join(" → ")}`
                  : `${i18n.t(groupField.label)} - ${i18n.t(
                      fieldDef.label || fieldDef.key
                    )}`,
            });
          });
        });
      }

      this.visibleFilterFields = visibleFields;
      this.ensureDateRangesForVisibleFields();
      await this.ensureUsersFilterDataLoaded();
    },

    toggleMoreFilters() {
      this.moreFiltersOpen = !this.moreFiltersOpen;
      if (this.moreFiltersOpen) {
        this.$nextTick(() => this.onMoreFiltersPanelOpened());
      }
    },

    closeColumnFilterPopup() {
      this.columnFilterPopupOpen = false;
      this.columnFilterPopupFieldKey = null;
      this.columnFilterPopupDraft = null;
      this.columnFilterPopupResolvedField = null;
    },

    /**
     * Resuelve la definición de filtro para una clave sin usar selectedFilterFields / visibleFilterFields.
     */
    async resolveFilterFieldDefinitionForKey(fieldKey) {
      const direct = this.nonGroupFields.find((f) => f.key === fieldKey);
      if (direct) {
        if (direct.type === "image") return null;
        return direct;
      }

      for (const field of this.nonGroupFields) {
        if (field.type !== "reference") continue;
        if (!fieldKey.startsWith(`${field.key}.`)) continue;

        if (!this.referenceSubFieldsCache[field.key] && field.ref) {
          await this.loadReferenceSubFields(field.ref, field.key);
        }

        const built = await resolveReferenceSubfieldFilterField(
          this.modelFields,
          fieldKey,
          (name) => this.getModelDefinitionForFilter(name)
        );
        if (built) {
          const leafDef = built.subFieldDef;
          if (
            leafDef &&
            leafDef.type === "image"
          ) {
            return null;
          }
          const intermediate = built.intermediateRefField;
          let label;
          if (intermediate && built.nestedFieldKey) {
            label = `${this.$t(field.label)} → ${this.$t(
              intermediate.label || built.subFieldKey
            )} → ${this.$t(leafDef.label || built.nestedFieldKey)}`;
          } else {
            label = `${this.$t(field.label)} → ${this.$t(
              leafDef.label || built.subFieldKey
            )}`;
          }
          return {
            key: built.key,
            label,
            type: "reference_subfield",
            parentField: field,
            subFieldKey: built.subFieldKey,
            nestedFieldKey: built.nestedFieldKey || undefined,
            subFieldType: built.subFieldType || "string",
            subFieldRef: built.subFieldRef || null,
            subFieldListType: built.subFieldListType || null,
            subFieldListRef: built.subFieldListRef || null,
            ref: field.ref,
            isArray: built.isArray,
          };
        }
        const subFieldKey = fieldKey.replace(`${field.key}.`, "");
        const subFieldDef =
          this.referenceSubFieldsCache?.[field.key]?.find(
            (f) => f.key === subFieldKey
          ) || {};
        if (subFieldDef.type === "image") {
          return null;
        }
        return {
          key: fieldKey,
          label: `${this.$t(field.label)} → ${this.$t(
            subFieldDef.label || subFieldKey
          )}`,
          type: "reference_subfield",
          parentField: field,
          subFieldKey: subFieldKey,
          subFieldType: subFieldDef.type || "string",
          subFieldRef: subFieldDef.ref || null,
          subFieldListType: subFieldDef.listType || null,
          subFieldListRef: subFieldDef.listRef || null,
          ref: field.ref,
        };
      }

      for (const groupField of this.groupFields) {
        if (!fieldKey.startsWith(`${groupField.key}.`)) continue;
        const pathSuffix = fieldKey.replace(`${groupField.key}.`, "");
        const pathParts = pathSuffix.split(".");
        const { fieldDef, labelParts } = this.resolveNestedGroupField(
          groupField,
          pathParts
        );
        if (!fieldDef || fieldDef.type === "group") continue;
        if (fieldDef.type === "image") continue;
        return {
          ...fieldDef,
          key: fieldKey,
          label:
            labelParts.length > 0
              ? `${i18n.t(groupField.label)} → ${labelParts.join(" → ")}`
              : `${i18n.t(groupField.label)} - ${i18n.t(
                  fieldDef.label || fieldDef.key
                )}`,
        };
      }

      return null;
    },

    /**
     * Cabecera de tabla dinámica: abre el popup sin tocar «Más filtros» hasta que el usuario aplique (applyColumnFilterPopup).
     */
    async openColumnFilterPopup(fieldKey) {
      if (!fieldKey) return;
      const field = await this.resolveFilterFieldDefinitionForKey(fieldKey);
      if (!field) return;

      this.columnFilterPopupResolvedField = field;
      await this.normalizeOperatorForField(field);
      if (
        field.type === "reference_subfield" &&
        field.subFieldType === "list"
      ) {
        await this.normalizeReferenceSubfieldListValue(field);
      }
      if (field.type === "reference" && field.ref) {
        if (field.ref === "users") {
          await this.ensureUsersFilterDataLoaded();
        } else {
          await this.loadReferenceOptions(field.key);
        }
      } else if (
        field.type === "reference_subfield" &&
        field.subFieldRef === "users"
      ) {
        await this.ensureUsersFilterDataLoaded();
      }

      this.columnFilterPopupFieldKey = fieldKey;
      this.initColumnFilterPopupDraft(fieldKey);
      this.columnFilterPopupOpen = true;
    },

    clonePopupDraftValue(val) {
      if (val === undefined) return null;
      if (val === null) return null;
      if (Array.isArray(val)) return val.slice();
      if (typeof val === "object") return { ...val };
      return val;
    },

    initColumnFilterPopupDraft(fieldKey) {
      const raw = Object.prototype.hasOwnProperty.call(this.filters, fieldKey)
        ? this.filters[fieldKey]
        : null;
      const tr = this.tempDateRanges[fieldKey] || { start: "", end: "" };
      this.columnFilterPopupDraft = {
        filterValue: this.clonePopupDraftValue(raw),
        filterOperator: this.filterOperators[fieldKey] || "=",
        tempDateRange: {
          start: tr.start || "",
          end: tr.end || "",
        },
      };
    },

    async applyColumnFilterPopup() {
      const field = this.columnFilterPopupField;
      const draft = this.columnFilterPopupDraft;
      if (!field || !draft) {
        this.closeColumnFilterPopup();
        return;
      }
      const key = field.key;
      this.$set(this.filters, key, this.clonePopupDraftValue(draft.filterValue));
      this.$set(this.filterOperators, key, draft.filterOperator || "=");
      if (this.isDateField(field)) {
        this.$set(this.tempDateRanges, key, {
          start: (draft.tempDateRange && draft.tempDateRange.start) || "",
          end: (draft.tempDateRange && draft.tempDateRange.end) || "",
        });
        this.commitDateFilter(key);
      }
      if (field.type === "reference_subfield" && field.subFieldType === "list") {
        await this.normalizeReferenceSubfieldListValue(field);
      }
      this.$set(this.appliedFilterFieldDefs, key, field);

      this.suppressFilterPreferencesOnce = true;
      this.$emit("update:selectedFilterFields", {
        ...(this.selectedFilterFields || {}),
        [key]: true,
      });
      await this.$nextTick();
      await this.$nextTick();

      this.moreFiltersOpen = true;
      this.emitFilters();
      this.$nextTick(() => this.onMoreFiltersPanelOpened());
      this.closeColumnFilterPopup();
    },

    onFilterPanelDateRangeInput(fieldKey, payload) {
      const part = payload && payload.part;
      const value = payload && payload.value;
      this.onDateRangeInput(fieldKey, part, value);
    },

    onColumnPopupDraftDebouncedInput(value) {
      if (!this.columnFilterPopupDraft) return;
      this.$set(this.columnFilterPopupDraft, "filterValue", value);
    },

    onColumnPopupDraftImmediateInput(value) {
      if (!this.columnFilterPopupDraft) return;
      const v = Array.isArray(value)
        ? value.slice()
        : this.clonePopupDraftValue(value);
      this.$set(this.columnFilterPopupDraft, "filterValue", v);
    },

    onColumnPopupDraftOperatorChange(op) {
      if (!this.columnFilterPopupDraft) return;
      this.$set(this.columnFilterPopupDraft, "filterOperator", op);
    },

    onColumnPopupDraftDateOperatorChange(op) {
      if (!this.columnFilterPopupDraft) return;
      this.$set(this.columnFilterPopupDraft, "filterOperator", op);
      if (op !== "between" && this.columnFilterPopupDraft.tempDateRange) {
        this.$set(this.columnFilterPopupDraft.tempDateRange, "end", "");
      }
    },

    onColumnPopupDraftDateRangeInput(payload) {
      if (!this.columnFilterPopupDraft) return;
      const part = payload && payload.part;
      const val = payload && payload.value;
      if (!this.columnFilterPopupDraft.tempDateRange) {
        this.$set(this.columnFilterPopupDraft, "tempDateRange", {
          start: "",
          end: "",
        });
      }
      this.$set(this.columnFilterPopupDraft.tempDateRange, part, val);
    },

    clearColumnPopupDraftDateRange() {
      if (!this.columnFilterPopupDraft) return;
      this.$set(this.columnFilterPopupDraft, "tempDateRange", {
        start: "",
        end: "",
      });
    },

    clearColumnPopupDraftFilter() {
      if (!this.columnFilterPopupDraft) return;
      this.$set(this.columnFilterPopupDraft, "filterValue", null);
      this.$set(this.columnFilterPopupDraft, "filterOperator", "=");
      this.$set(this.columnFilterPopupDraft, "tempDateRange", {
        start: "",
        end: "",
      });
    },

    onColumnPopupDraftRefSubfieldNumber(raw) {
      if (!this.columnFilterPopupDraft) return;
      const v = raw === "" ? null : Number(raw);
      this.$set(
        this.columnFilterPopupDraft,
        "filterValue",
        Number.isNaN(v) ? null : v
      );
    },

    onColumnPopupDraftShippingStatus(ev) {
      if (!this.columnFilterPopupDraft) return;
      const v = ev.target.value;
      if (v === "" || v == null) {
        this.$set(this.columnFilterPopupDraft, "filterValue", null);
        return;
      }
      const n = Number(v);
      this.$set(
        this.columnFilterPopupDraft,
        "filterValue",
        Number.isNaN(n) ? null : n
      );
    },

    /** Expuesto para la cabecera de tabla (estado visual del ícono de filtro). */
    isColumnFilterActive(fieldKey) {
      return this.isFilterRowActive(fieldKey);
    },

    async onMoreFiltersPanelOpened() {
      for (const field of this.visibleFilterFields) {
        this.normalizeOperatorForField(field);
        if (
          field.type === "reference_subfield" &&
          field.subFieldType === "list"
        ) {
          await this.normalizeReferenceSubfieldListValue(field);
        }
        if (field.type === "reference" && field.ref) {
          if (field.ref === "users") {
            await this.ensureUsersFilterDataLoaded();
          } else {
            await this.loadReferenceOptions(field.key);
          }
        } else if (
          field.type === "reference_subfield" &&
          field.subFieldRef === "users"
        ) {
          await this.ensureUsersFilterDataLoaded();
        }
      }
    },

    normalizeOperatorForField(field) {
      const filterKey = field.key;
      const currentOperator = this.filterOperators[filterKey] || "=";
      const validNumberOperators = [
        "=",
        ">",
        "<",
        ">=",
        "<=",
        "!=",
        "is_null",
        "is_not_null",
      ];
      const textOperators = ["contains", "startswith", "endswith", "regex"];
      const isNumberField =
        field.type === "number" ||
        (field.type === "reference_subfield" &&
          field.subFieldType === "number");
      const isTextField =
        ["string", "text", "varchar", "char", "autoincrement"].includes(
          field.type
        ) ||
        (field.type === "reference_subfield" &&
          ["string", "text", "varchar", "char"].includes(field.subFieldType));
      if (isNumberField && textOperators.includes(currentOperator)) {
        this.$set(this.filterOperators, filterKey, "=");
      }
      if (
        isTextField &&
        !validNumberOperators.includes(currentOperator) &&
        !textOperators.includes(currentOperator) &&
        currentOperator !== "="
      ) {
        this.$set(this.filterOperators, filterKey, "=");
      }
      if (this.isDateField(field) && !this.tempDateRanges[filterKey]) {
        const range = this.parseFilterValueToDateRange(this.filters[filterKey]);
        this.$set(this.tempDateRanges, filterKey, range);
        this.syncDateRangeOperator(filterKey, range);
      }
    },

    /**
     * Operador con el que aparece un filtro recién elegido o limpiado: «=»,
     * salvo que el campo diga otro (p. ej. "Saldo al" arranca en «entre»).
     */
    defaultFilterOperator(field) {
      return (field && field.filterDefaultOperator) || "=";
    },

    parseFilterValueToDateRange(val) {
      return legacyDateRangeFromValue(val);
    },

    /**
     * Un filtro guardado con las dos fechas es un rango: se marca como
     * «between» para que la UI muestre los dos inputs (antes el rango era
     * implícito y el operador quedaba en «=»).
     */
    syncDateRangeOperator(fieldKey, range) {
      if (!range || !range.start || !range.end) return;
      if (this.filterOperators[fieldKey] === "between") return;
      this.$set(this.filterOperators, fieldKey, "between");
    },

    ensureDateRangesForVisibleFields() {
      this.visibleFilterFields.forEach((f) => {
        if (this.isDateField(f)) {
          const range = this.parseFilterValueToDateRange(this.filters[f.key]);
          this.$set(this.tempDateRanges, f.key, range);
          this.syncDateRangeOperator(f.key, range);
        }
      });
    },

    /** Estado del filtro con el spec canónico (ver utils/columnFilterSpec). */
    isFilterRowActive(filterKey) {
      const field = this.getFilterFieldDefinition(filterKey);
      const spec = specFromLegacyState({
        value: this.filters[filterKey],
        operator: this.filterOperators[filterKey],
        dateRange: this.tempDateRanges[filterKey],
        isDateField: !!field && this.isDateField(field),
      });
      return isFilterSpecActive(spec) && !!this.getFilterDisplayText(filterKey);
    },

    onDateRangeInput(fieldKey, part, value) {
      if (!this.tempDateRanges[fieldKey]) {
        this.$set(this.tempDateRanges, fieldKey, { start: "", end: "" });
      }
      this.$set(this.tempDateRanges[fieldKey], part, value);
      if (this.dateCommitTimeouts[fieldKey]) {
        clearTimeout(this.dateCommitTimeouts[fieldKey]);
      }
      this.dateCommitTimeouts[fieldKey] = setTimeout(() => {
        this.commitDateFilter(fieldKey);
      }, 350);
    },

    onDateOperatorChange(fieldKey, operator) {
      this.$set(this.filterOperators, fieldKey, operator);
      // Solo «between» muestra la segunda fecha: al salir se descarta para que
      // no siga filtrando por un rango que el usuario ya no ve.
      if (operator !== "between" && this.tempDateRanges[fieldKey]) {
        this.$set(this.tempDateRanges[fieldKey], "end", "");
      }
      // Asegurar clave en filters para que is_null/is_not_null se emitan al backend
      if (
        (operator === "is_null" || operator === "is_not_null") &&
        !Object.prototype.hasOwnProperty.call(this.filters, fieldKey)
      ) {
        this.$set(this.filters, fieldKey, null);
      }
      this.commitDateFilter(fieldKey);
    },

    commitDateFilter(fieldKey) {
      const field = this.getFilterFieldDefinition(fieldKey);
      if (!field || !this.isDateField(field)) return;
      const dateRange = this.tempDateRanges[fieldKey] || {
        start: "",
        end: "",
      };
      const selectedOperator =
        this.filterOperators[fieldKey] || this.defaultFilterOperator(field);
      // El que muestra el selector es el que viaja al listado.
      if (!this.filterOperators[fieldKey]) {
        this.$set(this.filterOperators, fieldKey, selectedOperator);
      }
      let filterValue = null;
      if (
        selectedOperator === "is_null" ||
        selectedOperator === "is_not_null"
      ) {
        filterValue = null;
      } else if (selectedOperator === "between") {
        // Entre dos fechas: hacen falta las dos puntas para filtrar.
        filterValue =
          dateRange.start && dateRange.end
            ? `${dateRange.start}${LEGACY_DATE_RANGE_SEPARATOR}${dateRange.end}`
            : null;
      } else if (dateRange.start || dateRange.end) {
        // Los demás operadores comparan contra una sola fecha.
        filterValue = dateRange.start || dateRange.end;
      }
      this.$set(this.filters, fieldKey, filterValue);
      this.emitFilters();
    },

    scheduleFilterEmit(fieldKey) {
      if (this.filterEmitTimeouts[fieldKey]) {
        clearTimeout(this.filterEmitTimeouts[fieldKey]);
      }
      this.filterEmitTimeouts[fieldKey] = setTimeout(() => {
        this.emitFilters();
      }, 400);
    },

    onDebouncedFilterValueInput(fieldKey, value) {
      this.$set(this.filters, fieldKey, value);
      this.scheduleFilterEmit(fieldKey);
    },

    onReferenceSubfieldNumberInput(fieldKey, raw) {
      const v = raw === "" ? null : Number(raw);
      this.$set(this.filters, fieldKey, Number.isNaN(v) ? null : v);
      this.scheduleFilterEmit(fieldKey);
    },

    onFilterOperatorImmediate(fieldKey, operator) {
      this.$set(this.filterOperators, fieldKey, operator);
      // Sin valor previo, filters[key] no existía y emitFilters omitía is_null/is_not_null
      // (chip visible pero listado sin filtrar — p. ej. Customer's Commercial is empty).
      if (
        (operator === "is_null" || operator === "is_not_null") &&
        !Object.prototype.hasOwnProperty.call(this.filters, fieldKey)
      ) {
        this.$set(this.filters, fieldKey, null);
      }
      this.emitFilters();
    },

    onFilterValueImmediate(fieldKey, value) {
      this.$set(this.filters, fieldKey, value);
      this.emitFilters();
    },

    onShippingStatusFilterChange(fieldKey, ev) {
      const v = ev.target.value;
      if (v === "" || v == null) {
        this.onFilterValueImmediate(fieldKey, null);
        return;
      }
      const n = Number(v);
      this.onFilterValueImmediate(fieldKey, Number.isNaN(n) ? null : n);
    },

    async normalizeReferenceSubfieldListValue(field) {
      const filterKey = field.key;
      const currentValue = this.filters[filterKey];
      const listOptions = this.getListOptionsForSubField(field);
      if (
        currentValue &&
        typeof currentValue === "string" &&
        !currentValue.includes(",")
      ) {
        const foundItem = listOptions.find((opt) => {
          const optId = opt._id || opt.id;
          return optId && optId.toString() === currentValue.toString();
        });
        if (foundItem) {
          this.$set(this.filters, filterKey, [foundItem]);
        } else if (listOptions.length === 0) {
          await this.loadListsIfNeeded(field);
          const updatedListOptions = this.getListOptionsForSubField(field);
          const retryFoundItem = updatedListOptions.find((opt) => {
            const optId = opt._id || opt.id;
            return optId && optId.toString() === currentValue.toString();
          });
          if (retryFoundItem) {
            this.$set(this.filters, filterKey, [retryFoundItem]);
          }
        }
      } else if (
        currentValue &&
        typeof currentValue === "string" &&
        currentValue.includes(",")
      ) {
        const ids = currentValue.split(",").map((id) => id.trim());
        const foundItems = ids
          .map((id) => {
            const foundItem = listOptions.find((opt) => {
              const optId = opt._id || opt.id;
              return optId && optId.toString() === id.toString();
            });
            return foundItem || { _id: id };
          })
          .filter((item) => item);
        if (foundItems.length > 0) {
          this.$set(this.filters, filterKey, foundItems);
        }
      } else if (
        currentValue &&
        typeof currentValue === "object" &&
        !Array.isArray(currentValue)
      ) {
        this.$set(this.filters, filterKey, [currentValue]);
      } else if (!Array.isArray(currentValue) && currentValue) {
        const foundItem = listOptions.find((opt) => {
          const optId = opt._id || opt.id;
          return optId && optId.toString() === String(currentValue);
        });
        if (foundItem) {
          this.$set(this.filters, filterKey, [foundItem]);
        }
      }
    },

    async loadListsIfNeeded(field) {
      if (this.lists && this.lists.length > 0) return;
      try {
        const { data: listsData } = await wencesApi.get("/api/gotrading/lists");
        this.lists = listsData;
      } catch (e) {
        console.error("Error loading lists for filter:", e);
      }
    },

    clearFilter(filterKey) {
      const field = this.getFilterFieldDefinition(filterKey);
      this.$set(this.filters, filterKey, null);
      this.$set(
        this.filterOperators,
        filterKey,
        this.defaultFilterOperator(field)
      );

      // Limpiar también los rangos de fecha si es un campo de fecha (incluye reference_subfield)
      if (field && this.isDateField(field)) {
        this.$set(this.tempDateRanges, filterKey, { start: "", end: "" });
      }

      if (Object.prototype.hasOwnProperty.call(this.appliedFilterFieldDefs, filterKey)) {
        this.$delete(this.appliedFilterFieldDefs, filterKey);
      }

      this.emitFilters();
    },

    isUsersFilterField(field) {
      if (!field) return false;
      if (field.type === "reference" && field.ref === "users") return true;
      if (
        field.type === "reference_subfield" &&
        field.subFieldRef === "users"
      ) {
        return true;
      }
      return false;
    },

    syncUsersIntoReferenceOptions() {
      if (!this.usersOptions.length) return;
      for (const field of this.visibleFilterFields) {
        if (field.type === "reference" && field.ref === "users") {
          this.$set(
            this.internalReferenceOptions,
            field.key,
            this.usersOptions.slice()
          );
        }
      }
    },

    async ensureUsersFilterDataLoaded() {
      const needsUsers = this.visibleFilterFields.some((f) =>
        this.isUsersFilterField(f)
      );
      if (!needsUsers) return;
      await this.loadUsersOptions();
      this.syncUsersIntoReferenceOptions();
    },

    async onReferenceSearchChange(field, searchTerm) {
      if (this.isUsersFilterField(field)) {
        if (!this.usersOptions.length) {
          await this.ensureUsersFilterDataLoaded();
        }
        return;
      }

      // Limpiar el timeout anterior si existe
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      // Si el término de búsqueda está vacío o es muy corto, limpiar las opciones
      if (!searchTerm || searchTerm.trim().length < 2) {
        this.$set(this.internalReferenceOptions, field.key, []);
        return;
      }

      // Establecer un timeout para evitar demasiadas llamadas a la API
      this.searchTimeout = setTimeout(async () => {
        try {
          await this.searchReferences(field, searchTerm);
        } catch (error) {
          console.error("Error in reference search:", error);
        }
      }, 300); // Esperar 300ms después de que el usuario deje de escribir
    },

    async searchReferences(field, searchTerm) {
      try {
        // Marcar el campo como buscando
        this.searchingFields.add(field.key);

        const searchKey = `${field.ref}:${searchTerm.trim().toLowerCase()}`;

        // Verificar cache primero
        if (this.referenceSearchCache[searchKey]) {
          const cachedData = this.referenceSearchCache[searchKey];
          // Actualizar las opciones de referencia con datos cacheados
          const currentValue = this.filters[field.key];
          let finalOptions = [...cachedData];

          // Si hay un valor actual y no está en los resultados, agregarlo
          if (currentValue) {
            const currentId =
              typeof currentValue === "string"
                ? currentValue
                : currentValue._id || currentValue;
            const exists = finalOptions.some((opt) => opt._id === currentId);
            if (!exists && currentId) {
              finalOptions.push(
                typeof currentValue === "string"
                  ? { _id: currentValue, name: currentValue }
                  : currentValue
              );
            }
          }

          this.$set(this.internalReferenceOptions, field.key, finalOptions);
          this.searchingFields.delete(field.key);
          return;
        }

        let validData = [];

        if (field.ref === "users") {
          await this.ensureUsersFilterDataLoaded();
          const searchLower = searchTerm.trim().toLowerCase();
          validData = this.usersOptions.filter(
            (item) =>
              item && item.name && item.name.toLowerCase().includes(searchLower)
          );
        } else {
          // Construir el query string con los filtros
          const params = new URLSearchParams();

          // Usar el endpoint existente con regex para búsqueda dinámica
          params.append("name", `regex:${searchTerm.trim()}`);
          params.append("perPage", "20");

          if (field.refFilter && field.refFilterValue) {
            params.append(field.refFilter, field.refFilterValue);
          }

          const url = `/api/gotrading/dynamic/${
            field.ref
          }?${params.toString()}`;
          const { data } = await wencesApi.get(url);

          // Asegurarse de que los datos sean válidos
          validData = (data.items || data || []).filter(
            (item) => item && (item._id || item.name)
          );
        }

        // Guardar en cache (limitar tamaño del cache a 50 entradas)
        const cacheKeys = Object.keys(this.referenceSearchCache);
        if (cacheKeys.length >= 50) {
          // Eliminar la entrada más antigua (FIFO simple)
          delete this.referenceSearchCache[cacheKeys[0]];
        }
        this.referenceSearchCache[searchKey] = validData;

        // Obtener el valor actual para preservarlo en las opciones
        const currentValue = this.filters[field.key];
        let finalOptions = [...validData];

        // Si hay un valor actual y no está en los resultados de búsqueda, agregarlo
        if (currentValue) {
          const currentId =
            typeof currentValue === "string"
              ? currentValue
              : currentValue._id || currentValue;
          const exists = finalOptions.some((opt) => opt._id === currentId);
          if (!exists && currentId) {
            finalOptions.push(
              typeof currentValue === "string"
                ? { _id: currentValue, name: currentValue }
                : currentValue
            );
          }
        }

        // Actualizar las opciones de referencia
        this.$set(this.internalReferenceOptions, field.key, finalOptions);
      } catch (error) {
        console.error(`❌ Error searching references for ${field.key}:`, error);
        this.$set(this.internalReferenceOptions, field.key, []);
      } finally {
        // Remover el campo de la lista de búsqueda
        this.searchingFields.delete(field.key);
      }
    },

    emitFilters() {
      // Construir el objeto de filtros para emitir
      const filtersToEmit = {};

      // Agregar filtros normales con operadores
      Object.entries(this.filters).forEach(([key, value]) => {
        const operator = this.filterOperators[key] || "=";

        // Manejar operadores especiales sin valor
        if (operator === "is_null" || operator === "is_not_null") {
          filtersToEmit[key] = operator;
          return;
        }

        if (value !== null && value !== "") {
          // Obtener el campo para determinar el tipo (puede ser undefined para subcampos de group)
          const field = this.getFilterFieldDefinition(key);

          // Para campos tipo list y reference, manejar arrays de valores
          // Incluir también cuando no hay field (ej. subcampos de group como products.cut) pero el valor es objeto/array de referencias
          // Un texto escrito a mano (subcampo de texto, o de referencia sin selector:
          // «Invoices → Contact») va por la rama de operadores: acá se perdía el
          // operador y «Equal to», «Starts with» o «Different from» terminaban
          // funcionando todos como «Contains».
          const isReferenceSubfieldNonNumeric =
            field &&
            field.type === "reference_subfield" &&
            field.subFieldType !== "number" &&
            field.subFieldType !== "date" &&
            typeof value !== "string";
          const isReferenceLike =
            field &&
            (field.type === "list" ||
              field.type === "reference" ||
              isReferenceSubfieldNonNumeric);
          const isReferenceValueWithoutField =
            !field &&
            (Array.isArray(value) ||
              (typeof value === "object" && value !== null && value._id));

          if (isReferenceLike || isReferenceValueWithoutField) {
            // «Different from» viaja como «!=id1,id2»: sin el prefijo el backend
            // recibía los mismos ids que con «Equal to» y filtraba igual
            const negatePrefix = operator === "!=" ? "!=" : "";
            // Manejar arrays de valores para campos tipo list, reference y reference_subfield (no numéricos)
            // También subcampos de group (products.cut, etc.) que pueden no estar en visibleFilterFields en algún momento
            if (Array.isArray(value)) {
              // Extraer los IDs de cada elemento del array
              const ids = value
                .filter((item) => item !== null && item !== undefined)
                .map((item) => {
                  if (typeof item === "object" && item._id) {
                    return item._id;
                  }
                  return item;
                });

              // Si hay IDs, enviarlos como array o como string separado por comas
              if (ids.length > 0) {
                // Enviar como string separado por comas para compatibilidad con el backend
                filtersToEmit[key] = `${negatePrefix}${ids.join(",")}`;
              }
            } else {
              // Si es un solo valor, extraer el ID
              let filterValue = value;
              if (typeof value === "object" && value._id) {
                filterValue = value._id;
              }
              filtersToEmit[key] = negatePrefix
                ? `${negatePrefix}${filterValue}`
                : filterValue;
            }
          } else if (field && field.type === "boolean") {
            const boolValue = coerceBooleanFilterValue(value);
            if (typeof boolValue === "boolean") {
              filtersToEmit[key] = boolValue;
            }
          } else {
            // Para otros tipos de campos, usar la lógica original con operadores
            let filterValue = value;
            if (typeof value === "object" && value._id) {
              filterValue = value._id;
            }

            if (operator === "contains") {
              filtersToEmit[key] = `contains:${filterValue}`;
            } else if (operator === "startswith") {
              filtersToEmit[key] = `startswith:${filterValue}`;
            } else if (operator === "endswith") {
              filtersToEmit[key] = `endswith:${filterValue}`;
            } else if (operator === "regex") {
              filtersToEmit[key] = `regex:${filterValue}`;
            } else if (operator === "=") {
              // Texto de un subcampo de referencia: «=valor» para que el backend
              // compare exacto (un valor pelado ahí es «contiene», que es como lo
              // dejaron guardado los reportes viejos). El resto: solo el valor.
              const isTypedReferenceSubfield =
                field &&
                field.type === "reference_subfield" &&
                field.subFieldType !== "list" &&
                field.subFieldRef !== "users";
              filtersToEmit[key] = isTypedReferenceSubfield
                ? `=${filterValue}`
                : filterValue;
            } else if (
              operator === "between" ||
              String(filterValue).includes(LEGACY_DATE_RANGE_SEPARATOR)
            ) {
              // El rango ya viaja como «inicio - fin»: prefijarlo con el
              // operador rompía la fecha de inicio y se volvía a prefijar en
              // cada vuelta (betweenbetween2026-09-01 - 2026-09-30).
              filtersToEmit[key] = stripLegacyBetweenPrefix(filterValue);
            } else {
              // Para otros operadores (!=, >, <, >=, <=), concatenar operador + valor
              filtersToEmit[key] = `${operator}${filterValue}`;
            }
          }
        }
      });

      // Operadores vacíos/no vacíos que viven solo en filterOperators (sin entrada en filters)
      Object.entries(this.filterOperators || {}).forEach(([key, operator]) => {
        if (
          (operator === "is_null" || operator === "is_not_null") &&
          filtersToEmit[key] === undefined
        ) {
          filtersToEmit[key] = operator;
        }
      });

      // Agregar búsqueda de texto por textPlainVersion si existe
      if (this.textSearchQuery && this.textSearchQuery.trim()) {
        // Enviar solo el valor del texto, no el objeto completo
        filtersToEmit.textPlainVersion = this.textSearchQuery.trim();
      }

      this.$emit("filter", filtersToEmit);
    },

    getMultiselectLabel(option, fieldKey = null) {
      if (!option) return "No name";
      const refKey = fieldKey;

      if (typeof option === "string") {
        const optionFound = refKey
          ? this.internalReferenceOptions[refKey]?.find(
              (opt) => opt._id === option
            )
          : null;
        if (optionFound) {
          return optionFound.name || optionFound.label || String(option);
        }
        return String(option);
      }

      if (option._id == option.name) {
        const optionFound = refKey
          ? this.internalReferenceOptions[refKey]?.find(
              (opt) => opt._id === option._id
            )
          : null;
        return optionFound ? optionFound.name : String(option._id);
      }

      if (option.name) {
        return option.name;
      }

      if (option.label) {
        return option.label;
      }

      if (option._id && typeof option._id === "object") {
        return option._id.name || option._id.label || "No name";
      }

      if (typeof option === "string" || typeof option === "number") {
        return String(option);
      }

      if (option._id) {
        return String(option._id);
      }

      return "No name";
    },

    getFilterFieldDefinition(filterKey) {
      return (
        this.visibleFilterFields.find((f) => f.key === filterKey) ||
        this.appliedFilterFieldDefs[filterKey] ||
        null
      );
    },

    getFilterDisplayText(filterKey) {
      const value = this.filters[filterKey];
      const operator = this.filterOperators[filterKey] || "=";

      const field = this.getFilterFieldDefinition(filterKey);
      const fieldLabel = field ? this.$t(field.label) : filterKey;

      // Manejar operadores especiales sin valor
      if (operator === "is_null") {
        return `${fieldLabel}: is empty`;
      }
      if (operator === "is_not_null") {
        return `${fieldLabel}: not empty`;
      }

      // Comprobar si el valor está vacío, pero permitir false para booleanos
      if (value === null || value === undefined || value === "") return "";

      if (!field) {
        // Si no hay campo pero el valor es un objeto, intentar mostrar name o label
        if (typeof value === "object" && value !== null) {
          return `${fieldLabel}: ${
            value.name || value.label || JSON.stringify(value)
          }`;
        }
        return `${fieldLabel}: ${value}`;
      }

      // Formatear el operador para mostrar
      const operatorText = this.getOperatorText(operator);

      // Caso especial para status de shipping
      if (
        field.key === "status" &&
        this.modelName === "shipping" &&
        field.type === "number"
      ) {
        const statusLabel = getStatusLabel(Number(value), this.$t);
        if (operator !== "=" && operatorText) {
          return `${fieldLabel} ${operatorText} ${statusLabel}`;
        }
        return `${fieldLabel}: ${statusLabel}`;
      }

      // Formatear el valor según el tipo de campo
      let valueText = "";
      switch (field.type) {
        case "date":
          valueText = value;
          break;
        case "reference":
          // Manejar arrays de valores
          if (Array.isArray(value)) {
            const labels = value.map((item) => {
              if (typeof item === "object" && item !== null) {
                return item.name || item.label || item._id;
              } else {
                const refOptions =
                  this.internalReferenceOptions[filterKey] || [];
                const refItem = refOptions.find((opt) => opt._id === item);
                return refItem
                  ? this.getMultiselectLabel(refItem, filterKey)
                  : item;
              }
            });
            valueText = labels.join(", ");
          } else if (typeof value === "object" && value !== null) {
            valueText = value.name || value.label || value._id;
          } else {
            // Si es un ID, buscar en las opciones
            const refOptions = this.internalReferenceOptions[filterKey] || [];
            const refItem = refOptions.find((item) => item._id === value);
            valueText = refItem
              ? this.getMultiselectLabel(refItem, filterKey)
              : value;
          }
          break;
        case "reference_subfield":
          // Si el subcampo es de tipo list, buscar el label en las listas
          if (
            field.subFieldType === "list" &&
            (field.subFieldListType || field.subFieldListRef)
          ) {
            // Buscar la lista correcta
            let listOptions = [];

            // Primero intentar con subFieldListRef
            if (field.subFieldListRef) {
              if (
                this.referenceOptions &&
                this.referenceOptions[field.subFieldListRef]
              ) {
                listOptions = this.referenceOptions[field.subFieldListRef];
              }
            }

            // Si no se encontró, intentar con subFieldListType
            if (listOptions.length === 0 && field.subFieldListType) {
              if (this.lists && this.lists.length > 0) {
                const list = this.lists.find(
                  (l) => l.type === field.subFieldListType
                );
                if (list && list.values && Array.isArray(list.values)) {
                  listOptions = list.values;
                }
              }
            }

            // Debug: log para ver qué está pasando
            if (listOptions.length === 0) {
              console.log(
                "[getFilterDisplayText] No se encontraron opciones de lista para:",
                {
                  filterKey,
                  subFieldType: field.subFieldType,
                  subFieldListType: field.subFieldListType,
                  subFieldListRef: field.subFieldListRef,
                  hasLists: !!(this.lists && this.lists.length > 0),
                  listsCount: this.lists ? this.lists.length : 0,
                  hasReferenceOptions: !!(
                    this.referenceOptions &&
                    Object.keys(this.referenceOptions).length > 0
                  ),
                  value: value,
                }
              );
            }

            // Si aún no hay opciones y las listas no están cargadas, intentar cargarlas
            if (
              listOptions.length === 0 &&
              (!this.lists || this.lists.length === 0)
            ) {
              // Cargar listas de forma síncrona si es posible (pero esto puede no funcionar en el template)
              // Por ahora, retornar el ID y cargar en background
              this.loadListsIfNeeded(field);
              valueText =
                typeof value === "object" && value !== null
                  ? value.label || value._id || value
                  : value;
            } else if (listOptions.length === 0) {
              // Las listas están cargadas pero no se encontró la lista específica
              valueText =
                typeof value === "object" && value !== null
                  ? value.label || value._id || value
                  : value;
            } else {
              // Manejar arrays de valores
              if (Array.isArray(value)) {
                const labels = value.map((item) => {
                  // Si ya es un objeto con label, usarlo
                  if (typeof item === "object" && item !== null && item.label) {
                    return item.label;
                  }
                  // Si es un ID, buscar en las listas
                  const listValueId =
                    typeof item === "object" && item !== null
                      ? item._id || item.id
                      : item;
                  const listItem = listOptions.find((opt) => {
                    const optId = opt._id || opt.id;
                    return optId && optId.toString() === String(listValueId);
                  });
                  return listItem
                    ? listItem.label
                    : typeof item === "object"
                    ? item.name || item.label || item._id
                    : item;
                });
                valueText = labels.join(", ");
              } else {
                // Si ya es un objeto con label, usarlo
                if (
                  typeof value === "object" &&
                  value !== null &&
                  value.label
                ) {
                  valueText = value.label;
                } else {
                  // Si es un ID, buscar en las listas
                  const listValueId =
                    typeof value === "object" && value !== null
                      ? value._id || value.id
                      : value;
                  const listItem = listOptions.find((opt) => {
                    const optId = opt._id || opt.id;
                    return optId && optId.toString() === String(listValueId);
                  });
                  valueText = listItem
                    ? listItem.label
                    : typeof value === "object"
                    ? value.name || value.label || value._id
                    : value;
                }
              }
            }
          } else {
            // Manejar arrays de valores (para otros tipos que no sean list)
            if (Array.isArray(value)) {
              const labels = value.map((item) => {
                if (typeof item === "object" && item !== null) {
                  return item.name || item.label || item._id;
                } else if (field.subFieldRef === "users") {
                  const userItem = this.usersOptions.find(
                    (opt) => opt._id === item
                  );
                  return userItem ? userItem.name || userItem.label : item;
                } else {
                  return item;
                }
              });
              valueText = labels.join(", ");
            } else if (typeof value === "object" && value !== null) {
              valueText = value.name || value.label || value._id;
            } else if (field.subFieldRef === "users") {
              // Si es un ID y el subcampo apunta a users, buscar en usersOptions
              const userItem = this.usersOptions.find(
                (item) => item._id === value
              );
              valueText = userItem ? userItem.name || userItem.label : value;
            } else {
              valueText = value;
            }
          }
          break;
        case "list":
          // Manejar arrays de valores
          if (Array.isArray(value)) {
            const listOptions = this.getListOptions(field);
            const labels = value.map((item) => {
              const listValue = typeof item === "object" ? item._id : item;
              const listItem = listOptions.find(
                (opt) => String(opt._id) === String(listValue)
              );
              return listItem
                ? listItem.label
                : typeof item === "object"
                ? item.label
                : item;
            });
            valueText = labels.join(", ");
          } else {
            // Si el valor es un objeto, usar su _id para buscar
            const listValue = typeof value === "object" ? value._id : value;
            const listOptions = this.getListOptions(field);
            const listItem = listOptions.find(
              (item) => String(item._id) === String(listValue)
            );
            valueText = listItem
              ? listItem.label
              : typeof value === "object"
              ? value.label
              : value;
          }
          break;
        case "textlist":
          // Obtener opciones inline para textlist
          if (field.options && typeof field.options === "string") {
            const options = field.options
              .split(",")
              .map((option) => option.trim())
              .filter((option) => option);
            const textListItem = options.find((option) => option === value);
            valueText = textListItem || value;
          } else {
            valueText = value.label || value;
          }
          break;
        case "boolean": {
          const boolValue = coerceBooleanFilterValue(value);
          valueText = boolValue === true ? "Yes" : boolValue === false ? "No" : value;
          break;
        }
        default:
          // Para cualquier otro tipo, si es objeto mostrar name/label
          if (typeof value === "object" && value !== null) {
            valueText = value.name || value.label || JSON.stringify(value);
          } else {
            valueText = value;
          }
      }

      // Si el operador no es "=", incluir el operador en el texto
      if (operator !== "=" && operatorText) {
        return `${fieldLabel} ${operatorText} ${valueText}`;
      }
      return `${fieldLabel}: ${valueText}`;
    },
    getOperatorText(operator) {
      const operatorMap = {
        ">": ">",
        "<": "<",
        ">=": "≥",
        "<=": "≤",
        "!=": "≠",
        contains: "contains",
        startswith: "starts with",
        endswith: "ends with",
        regex: "matches",
      };
      return operatorMap[operator] || operator;
    },
    getListOptions(field) {
      // Si tiene listRef, buscar en las opciones de referencia
      if (field.listRef) {
        if (this.referenceOptions && this.referenceOptions[field.listRef]) {
          return this.normalizeListOptionIds(
            this.referenceOptions[field.listRef]
          );
        }
      }

      // Si tiene listType, buscar en las listas internas
      if (field.listType) {
        const list = this.lists.find((l) => l.type === field.listType);
        if (list && list.values) {
          return this.normalizeListOptionIds(list.values);
        }
      }

      // Si no tiene listRef ni listType, intentar buscar por el nombre del campo
      if (this.lists) {
        const list = this.lists.find((l) => l.type === field.key);
        if (list && list.values) {
          return this.normalizeListOptionIds(list.values);
        }
      }

      // Si no se encontraron opciones, crear opciones por defecto para campos comunes
      const defaultOptions = {
        status: [
          { _id: "active", label: "Active" },
          { _id: "inactive", label: "Inactive" },
          { _id: "pending", label: "Pending" },
        ],
        priority: [
          { _id: "low", label: "Low" },
          { _id: "medium", label: "Medium" },
          { _id: "high", label: "High" },
        ],
        type: [
          { _id: "type1", label: "Type 1" },
          { _id: "type2", label: "Type 2" },
          { _id: "type3", label: "Type 3" },
        ],
      };

      if (defaultOptions[field.key]) {
        return defaultOptions[field.key];
      }

      return [];
    },
    getListOptionsForSubField(field) {
      // Método helper para obtener opciones de lista para un subcampo de referencia
      // Primero intentar usar subFieldListType o subFieldListRef
      if (field.subFieldListRef) {
        if (
          this.referenceOptions &&
          this.referenceOptions[field.subFieldListRef]
        ) {
          return this.normalizeListOptionIds(
            this.referenceOptions[field.subFieldListRef]
          );
        }
      }

      if (field.subFieldListType) {
        const list = this.lists.find((l) => l.type === field.subFieldListType);
        if (list && list.values) {
          return this.normalizeListOptionIds(list.values);
        }
      }

      // Si no se encontraron opciones, retornar array vacío
      return [];
    },

    /** Alinea _id a string para matchear filtros de URL/reporte con opciones de lista. */
    normalizeListOptionIds(options) {
      if (!Array.isArray(options)) return [];
      return options.map((opt) => {
        if (!opt || typeof opt !== "object" || opt._id == null) return opt;
        const id = String(opt._id);
        return id === opt._id ? opt : { ...opt, _id: id };
      });
    },

    /** Evita "undefined" en vue-multiselect cuando el valor es solo un id (p. ej. tras recargar la URL). */
    listMultiselectCustomLabel(option, field, subFieldList = false) {
      if (option == null || option === "") {
        return "";
      }
      if (typeof option === "string" || typeof option === "number") {
        const opts = subFieldList
          ? this.getListOptionsForSubField(field)
          : this.getListOptions(field);
        const hit = opts.find((x) => String(x._id) === String(option));
        return hit ? hit.label || hit.name || String(hit._id) : String(option);
      }
      if (typeof option === "object") {
        const fromLabel = option.label || option.name;
        if (fromLabel && fromLabel !== option._id) {
          return fromLabel;
        }
        const id = option._id;
        if (id != null && id !== "") {
          const opts = subFieldList
            ? this.getListOptionsForSubField(field)
            : this.getListOptions(field);
          const hit = opts.find((x) => String(x._id) === String(id));
          if (hit) return hit.label || hit.name || String(hit._id);
          return fromLabel || String(id);
        }
        return "";
      }
      return String(option);
    },

    async loadData() {
      try {
        // Cargar listas (necesarias para mostrar labels de filtros aplicados)
        const { data: listsData } = await wencesApi.get("/api/gotrading/lists");
        this.lists = listsData;

        // Cargar opciones de referencia pasadas como prop
        if (this.referenceOptions) {
          this.internalReferenceOptions = { ...this.referenceOptions };
        }

        // NO precargar todas las referencias - ahora se cargan bajo demanda (lazy loading)
        // Solo cargar referencias que ya tienen un valor seleccionado (para mostrar el valor actual)
        await this.loadReferenceOptions();

        // Las preferencias de filtros se cargarán en el watcher de selectedFilterFields
        // para asegurar que los campos estén inicializados primero
      } catch (error) {
        console.error("❌ Error loading filter data:", error);
      }
    },

    async loadFilterPreferences() {
      try {
        const { data } = await wencesApi.get(
          `/api/users/filter-preferences/${this.modelName}`
        );

        if (data.data && data.data.preferences) {
          const preferences = data.data.preferences;

          // Aplicar filtros guardados
          if (preferences.filters) {
            this.filters = { ...preferences.filters };
          }

          // Aplicar operadores guardados
          if (preferences.operators) {
            this.filterOperators = { ...preferences.operators };

            // Validar y corregir operadores según el tipo de campo
            const validNumberOperators = [
              "=",
              ">",
              "<",
              ">=",
              "<=",
              "!=",
              "is_null",
              "is_not_null",
            ];
            const textOperators = [
              "contains",
              "startswith",
              "endswith",
              "regex",
            ];

            Object.keys(this.filterOperators).forEach((key) => {
              const field = this.visibleFilterFields.find((f) => f.key === key);
              if (field) {
                const operator = this.filterOperators[key];

                // Determinar si es un campo numérico (incluyendo reference_subfield con subFieldType number)
                const isNumberField =
                  field.type === "number" ||
                  (field.type === "reference_subfield" &&
                    field.subFieldType === "number");

                // Determinar si es un campo de texto
                const isTextField =
                  [
                    "string",
                    "text",
                    "varchar",
                    "char",
                    "autoincrement",
                  ].includes(field.type) ||
                  (field.type === "reference_subfield" &&
                    ["string", "text", "varchar", "char"].includes(
                      field.subFieldType
                    ));

                // Si es un campo numérico y tiene un operador de texto, corregirlo
                if (isNumberField && textOperators.includes(operator)) {
                  this.$set(this.filterOperators, key, "=");
                }

                // Si es un campo de texto y tiene un operador numérico inválido, corregirlo
                if (
                  isTextField &&
                  !validNumberOperators.includes(operator) &&
                  !textOperators.includes(operator) &&
                  operator !== "="
                ) {
                  this.$set(this.filterOperators, key, "=");
                }
              }
            });
          }

          this.ensureDateRangesForVisibleFields();
          if (this.hasActiveFilters) {
            this.moreFiltersOpen = true;
            this.emitFilters();
            this.$nextTick(() => this.onMoreFiltersPanelOpened());
          }
        } else {
        }
      } catch (error) {
        console.error("❌ Error loading filter preferences:", error);
      }
    },

    async loadReferenceOptions(fieldKey = null) {
      try {
        // Si se especifica un fieldKey, solo cargar ese campo (lazy loading)
        if (fieldKey) {
          const field = this.visibleFilterFields.find(
            (f) => f.key === fieldKey
          );
          if (field && field.type === "reference" && field.ref) {
            if (field.ref === "users") {
              await this.ensureUsersFilterDataLoaded();
              return;
            }
            // Verificar si ya está cargado
            if (
              this.internalReferenceOptions[fieldKey] &&
              Array.isArray(this.internalReferenceOptions[fieldKey]) &&
              this.internalReferenceOptions[fieldKey].length > 0
            ) {
              return; // Ya está cargado
            }

            await this.loadSingleReferenceField(field);
          }
          return;
        }

        await this.ensureUsersFilterDataLoaded();

        // Cargar referencias no-usuario si ya hay un valor seleccionado (mostrar etiqueta actual)
        const referenceFields = this.visibleFilterFields.filter(
          (field) => field.type === "reference" && field.ref !== "users"
        );

        for (const field of referenceFields) {
          if (field.ref && this.filters[field.key]) {
            await this.loadSingleReferenceField(field);
          }
        }
      } catch (error) {
        console.error("❌ Error in loadReferenceOptions:", error);
      }
    },

    async loadSingleReferenceField(field) {
      try {
        if (field.ref === "users") {
          await this.ensureUsersFilterDataLoaded();
          this.$set(
            this.internalReferenceOptions,
            field.key,
            this.usersOptions.slice()
          );
          return;
        }

        const endpoint = `/api/gotrading/dynamic/${field.ref}?perPage=100`;

        // Cargar opciones desde la API
        const { data } = await wencesApi.get(endpoint);

        if (data && data.items) {
          this.$set(this.internalReferenceOptions, field.key, data.items);
        } else {
          this.$set(this.internalReferenceOptions, field.key, []);
        }
      } catch (error) {
        console.error(`❌ Error loading options for ${field.key}:`, error);
        this.$set(this.internalReferenceOptions, field.key, []);
      }
    },

    async saveFilterPreferences() {
      // Este método está deshabilitado temporalmente
      // Los filtros se guardan desde el componente padre
      return;
    },

    clearDateRange(fieldKey) {
      this.$set(this.tempDateRanges, fieldKey, { start: "", end: "" });
      this.$set(this.filters, fieldKey, null);
      this.emitFilters();
    },

    // Método público para que el componente padre pueda guardar preferencias
    saveFilterPreferencesFromParent() {
      // Este método está deshabilitado temporalmente
      return;
    },

    onTextSearchChange() {
      // Limpiar el timeout anterior si existe
      if (this.textSearchTimeout) {
        clearTimeout(this.textSearchTimeout);
      }

      // Debounce: esperar 500ms después de que el usuario deje de escribir
      this.textSearchTimeout = setTimeout(() => {
        this.emitFilters();
      }, 500);
    },

    applyTextSearch() {
      // Limpiar el timeout si existe (para aplicar inmediatamente al presionar Enter)
      if (this.textSearchTimeout) {
        clearTimeout(this.textSearchTimeout);
      }
      this.emitFilters();
    },

    clearTextSearch() {
      // Limpiar el timeout si existe
      if (this.textSearchTimeout) {
        clearTimeout(this.textSearchTimeout);
      }
      this.textSearchQuery = "";
      if (this.hideKeywordToolbar) {
        this.$emit("update:keywordQuery", "");
      }
      this.emitFilters();
    },

    /** Click en el chip: abre el mismo popup de filtro que el embudo del encabezado. */
    onAppliedChipClick(key) {
      if (!key || key === "__keyword") return;
      this.openColumnFilterPopup(key);
    },
    onAppliedChipClear(key) {
      if (key === "__keyword") {
        this.clearTextSearch();
        return;
      }
      this.clearFilter(key);
    },

    async loadUsersOptions() {
      if (this.usersOptions.length > 0) return;
      if (this._usersOptionsLoadPromise) {
        return this._usersOptionsLoadPromise;
      }

      this.usersOptionsLoading = true;
      this._usersOptionsLoadPromise = (async () => {
        try {
          const { data } = await wencesApi.get("/api/users/all");
          const usersArray =
            data && data.data ? data.data : Array.isArray(data) ? data : [];
          this.usersOptions = usersArray.filter((user) => user && user._id);
        } catch (error) {
          console.error("Error loading users options:", error);
          this.usersOptions = [];
        } finally {
          this.usersOptionsLoading = false;
          this._usersOptionsLoadPromise = null;
        }
      })();

      return this._usersOptionsLoadPromise;
    },

    async loadReferenceSubFields(refModelName, parentFieldKey) {
      if (this.referenceSubFieldsCache[parentFieldKey]) return; // Ya cargado

      try {
        const response = await wencesApi.get(
          `/api/gotrading/model-definitions/${refModelName}`
        );
        if (response.data && response.data.fields) {
          this.$set(
            this.referenceSubFieldsCache,
            parentFieldKey,
            response.data.fields
          );
        }
      } catch (error) {
        console.error(
          `Error loading reference sub fields for ${refModelName}:`,
          error
        );
      }
    },
  },
};
</script>

<style scoped>
.filters-container {
  margin-bottom: 1rem;
  clear: both;
  width: 100%;
}

.applied-filters-toolbar {
  border-color: #e9ecef !important;
}

.applied-filters-toolbar-inner {
  max-height: 6rem;
  overflow-y: auto;
}

.applied-filter-chip {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  padding: 0.2rem 0.35rem 0.2rem 0.5rem;
  font-size: 0.8125rem;
  font-weight: 400;
}

.applied-filter-chip--editable {
  cursor: pointer;
}
.applied-filter-chip-text {
  max-width: 14rem;
  vertical-align: middle;
}

/*
 * Popup filtro por columna: vue-multiselect usa z-index 50; el modal Bootstrap va ~1055.
 * Sin overflow visible / sin scrollable en el diálogo, el listado queda recortado detrás del pie.
 */
.gt-column-filter-modal {
  z-index: 1055;
}

.gt-column-filter-modal-dialog {
  /* Evita overflow:hidden de .modal-dialog-scrollable que recorta el dropdown */
  max-height: calc(100vh - 2rem);
}

.gt-column-filter-modal-dialog .modal-content {
  max-height: calc(100vh - 2rem);
  display: flex;
  flex-direction: column;
  /* Bootstrap suele poner overflow oculto; recorta el panel del multiselect (position absolute). */
  overflow: visible;
}

.gt-column-filter-modal-dialog .modal-body {
  /* Con overflow auto el listado del multiselect queda recortado al abrir hacia abajo. */
  overflow: visible;
}

.gt-column-filter-modal :deep(.multiselect--active) {
  z-index: 10060 !important;
}

.gt-column-filter-modal :deep(.multiselect__content-wrapper) {
  z-index: 10061 !important;
}

@media (min-width: 768px) {
  .applied-filter-chip-text {
    max-width: 18rem;
  }
}

.applied-filter-chip-remove {
  line-height: 1;
  text-decoration: none !important;
}

.applied-filter-chip-remove:hover,
.applied-filter-chip-remove:focus {
  color: #fff !important;
  opacity: 1;
}

/*
 * Reparto operador + multiselect: definido en FiltersFieldControls.vue (scoped del hijo).
 * Los selectores aquí no llegaban al <select> interno; además BS5 pone flex:1 en .input-group > .form-select.
 */

.filter-button {
  transition: all 0.2s ease;
}

.filter-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.filter-button.active {
  color: white;
}

.active-filters {
  padding: 12px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.available-filters {
  padding: 8px 0;
}

.gap-2 > * {
  margin-right: 0.5rem;
  margin-bottom: 0.5rem;
}

.gap-2 > *:last-child {
  margin-right: 0;
}

/* Estilos para el popup */
.filter-popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.3);
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
}

.filter-popup-content {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  width: 90%;
  max-width: 400px;
  max-height: 80vh;
  overflow-y: auto;
}

.filter-popup-header {
  padding: 16px 20px;
  border-bottom: 1px solid #e9ecef;
  background-color: #f8f9fa;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.filter-popup-header h6 {
  color: #495057;
  font-weight: 600;
  margin: 0;
}

.btn-close {
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
}

.btn-close:hover {
  background-color: #e9ecef;
  color: #495057;
}

.filter-popup-body {
  padding: 20px;
}

.filter-popup-footer {
  padding: 16px 20px;
  border-top: 1px solid #e9ecef;
  background-color: #f8f9fa;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

/* Estilos para opciones booleanas */
.boolean-options {
  display: flex;
  gap: 12px;
}

.boolean-option {
  flex: 1;
  display: flex;
  align-items: center;
  padding: 12px;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
  justify-content: center;
}

.boolean-option:hover {
  background-color: #f8f9fa;
  border-color: #17a2b8;
}

.boolean-option.selected {
  background-color: #17a2b8;
  border-color: #17a2b8;
  color: white;
}

.boolean-icon {
  width: 24px;
  height: 24px;
  background-color: #6c757d;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
}

.boolean-name {
  font-weight: 500;
}

.boolean-check {
  color: #28a745;
  margin-left: 8px;
}

/* Estilos para campos de formulario */
.form-label {
  font-weight: 500;
  color: #495057;
  margin-bottom: 8px;
}

.form-control-sm {
  font-size: 0.875rem;
}

/* FieldText / FieldNumber en modo filtro: grupo interno a ancho completo */
.string-filter .field-text,
.number-filter .field-number {
  width: 100%;
}

/* Estilos específicos para multiselect */
.multiselect {
  min-height: 40px;
  border: 1px solid #ced4da;
  border-radius: 4px;
}

.multiselect__tags {
  min-height: 40px;
  padding: 10px 31px 0 4px;
  border-radius: 4px;
  background: #fff;
}

.multiselect__input {
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
  min-height: 20px;
}

.multiselect__placeholder {
  color: #6c757d;
  margin-bottom: 0;
  padding-top: 0;
  padding-bottom: 0;
}

.multiselect__content-wrapper {
  border: 1px solid #ced4da;
  border-top: none;
  background: #fff;
  border-radius: 0 0 4px 4px;
  max-height: 200px;
  overflow-y: auto;
}

.multiselect__option {
  padding: 8px 12px;
  cursor: pointer;
}

.multiselect__option--highlight {
  background: #007bff;
  color: #fff;
}

.multiselect__option--selected {
  background: #e9ecef;
}

/* Estilos para filtros de subcampos de referencia */
.reference-subfield-filter {
  background-color: #f8f9fa;
  border-radius: 6px;
  padding: 12px;
}

.reference-path-indicator {
  font-size: 0.85rem;
}

.reference-path-indicator .badge {
  font-weight: normal;
}
</style>
